import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Tooltip, Menu, Box, Chip, Avatar, Typography
} from '@mui/material';
import {
  MoreVert, Edit, VpnKey, Search, Delete, Image, LocationOn, AttachMoney
} from '@mui/icons-material';
import ShopImageModal from './ShopImageModal';
import { useErrorStatus, ErrorIndicatorIcon } from '../indicator-errors/ErrorIndicator';




const ExhibitorProductTable = ({ products, onEdit, onAddKeyExhibitor, onAddSeo, onDeleteProduct }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedProductIdForImages, setSelectedProductIdForImages] = useState({});

  const status = useErrorStatus(selectedProduct?.woocommerce_product_id);

  const handleMenuClick = (event, product) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProduct(null);
  };

  const handleOpenImageModal = (productIds) => {
    setSelectedProductIdForImages(productIds);
    setImageModalOpen(true);
    handleMenuClose();
  };

  const handleCloseImageModal = () => setImageModalOpen(false);

  const getProductTypeColor = (type) => {
    const colors = {
      'Conference': '#1976d2',
      'Exhibition': '#388e3c',
      'Workshop': '#f57c00',
      'Seminar': '#7b1fa2',
      'Trade Show': '#d32f2f',
      'default': '#616161'
    };
    return colors[type] || colors.default;
  };

  return (
    <Box sx={{
      padding: { xs: '16px', md: '24px' },
      backgroundColor: '#f8fafc',
      minHeight: '70vh'
    }}>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 600,
          overflowY: 'auto',
          overflowX: 'auto',  // Enable horizontal scroll on small screens
          backgroundColor: '#fff',
          borderRadius: 3,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e2e8f0',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 20px 30px rgba(0, 0, 0, 0.12), 0 10px 15px rgba(0, 0, 0, 0.08)',
          },
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f5f9',
            borderRadius: 4
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#94a3b8',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: '#64748b'
            }
          }
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'auto',  // change from fixed to auto for better scroll & wrapping
            minWidth: 800,         // force minimum width so horizontal scroll appears on narrow screens
            width: '100%',
          }}
        >
          <TableHead>
            <TableRow>
              {[
                { label: "ID", width: "5%" },
                { label: "Event Name", width: "22%" },
                { label: "Background", width: "8%" },
                { label: "Location", width: "15%", icon: <LocationOn fontSize="small" /> },
                { label: "Exhibitors", width: "6%" },
                { label: "Price", width: "10%", icon: <AttachMoney fontSize="small" /> },
                { label: "Banner", width: "5%" },
                { label: "Type", width: "5%" },
                { label: "Actions", width: "8%" }
              ].map((header, index) => (
                <TableCell
                  key={index}
                  sx={{
                    backgroundColor: '#f8fafc',
                    color: '#374151',
                    fontWeight: '700',
                    width: header.width,
                    padding: { xs: '8px 6px', md: '16px 12px' }, // Responsive padding
                    fontSize: { xs: '0.75rem', md: '0.9rem' },   // Responsive font size
                    textAlign: 'center',
                    borderBottom: '2px solid #e5e7eb',
                    position: 'sticky',
                    top: 0,
                    zIndex: 11,
                    userSelect: 'none',
                    whiteSpace: 'nowrap',  // prevent wrapping
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    {header.icon && <Box sx={{ color: '#6b7280' }}>{header.icon}</Box>}
                    <Typography variant="body2" fontWeight="700" color="#374151" sx={{ userSelect: 'none' }}>
                      {header.label}
                    </Typography>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {products && products.map((product, i) => (
              <TableRow
                key={i}
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: '#ffffff' },
                  '&:nth-of-type(even)': { backgroundColor: '#f9fafb' },
                  borderLeft: '3px solid transparent',
                  transition: 'background-color 0.2s ease, border-left-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#f3f4f6',
                    borderLeftColor: '#2563eb',
                    '& .MuiTableCell-root': { borderColor: '#e2e8f0' }
                  },
                }}
              >
                <TableCell sx={{
                  padding: { xs: '8px 6px', md: '12px 8px' },
                  fontSize: { xs: '0.75rem', md: '0.9rem' },
                  textAlign: 'center',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                }}>
                  <Chip
                    label={i + 1}
                    size="small"
                    variant="outlined"
                    sx={{
                      backgroundColor: '#f1f5f9',
                      borderColor: '#d1d5db',
                      color: '#374151',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      height: 26,
                      minWidth: 34,
                      userSelect: 'none'
                    }}
                  />
                </TableCell>

           <TableCell sx={{
  padding: { xs: '8px 6px', md: '12px 8px' },
  fontSize: { xs: '0.75rem', md: '0.9rem' },
  maxWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}}>
  <Tooltip title={product.event_name} arrow placement="top">
    <Typography
      variant="body2"
      component="a"
      href={`https://exhibitorsdata.com/product/${product.slug}`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        fontWeight: '700',
        color: '#111827',
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
          color: '#2563eb',
          textDecoration: 'underline',
          transition: 'color 0.3s ease'
        }
      }}
    >
      {product.event_name}
    </Typography>
  </Tooltip>
</TableCell>


                <TableCell sx={{ padding: { xs: '8px 6px', md: '12px 8px' }, textAlign: 'center' }}>
                  <Avatar
                    src={product.background_img}
                    alt="Background"
                    variant="rounded"
                    sx={{
                      width: 50,
                      height: 34,
                      borderRadius: 2,
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#fafafa',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.08)',
                        boxShadow: '0 8px 15px rgba(0,0,0,0.12)'
                      }
                    }}
                  />
                </TableCell>

                <TableCell sx={{
                  padding: { xs: '8px 6px', md: '12px 8px' },
                  fontSize: { xs: '0.75rem', md: '0.9rem' },
                  maxWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  <Tooltip title={product.location} arrow>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <LocationOn fontSize="small" sx={{ color: '#6b7280' }} />
                      <Typography variant="body2" color="#4b5563">{product.location}</Typography>
                    </Box>
                  </Tooltip>
                </TableCell>

                <TableCell sx={{
                  padding: { xs: '8px 6px', md: '12px 8px' },
                  fontSize: { xs: '0.75rem', md: '0.9rem' },
                  textAlign: 'center'
                }}>
                  <Chip
                    label={product.total_exhibitor}
                    size="small"
                    sx={{
                      backgroundColor: '#d1fae5',
                      color: '#065f46',
                      fontWeight: '700',
                      border: '1px solid #a7f3d0',
                      fontSize: '0.75rem',
                      userSelect: 'none'
                    }}
                  />
                </TableCell>

                <TableCell sx={{
                  padding: { xs: '8px 6px', md: '12px 8px' },
                  fontSize: { xs: '0.75rem', md: '0.9rem' },
                  textAlign: 'center'
                }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <AttachMoney fontSize="small" sx={{ color: '#059669' }} />
                    <Typography variant="body2" fontWeight="700" color="#059669">{product.price}</Typography>
                  </Box>
                </TableCell>

                <TableCell sx={{ padding: { xs: '8px 6px', md: '12px 8px' }, textAlign: 'center' }}>
                  <Avatar
                    src={product.banner_img}
                    alt="Banner"
                    variant="rounded"
                    sx={{
                      width: 50,
                      height: 34,
                      borderRadius: 2,
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#fafafa',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.08)',
                        boxShadow: '0 8px 15px rgba(0,0,0,0.12)'
                      }
                    }}
                  />
                </TableCell>

                <TableCell sx={{
                  padding: { xs: '8px 6px', md: '12px 8px' },
                  fontSize: { xs: '0.75rem', md: '0.9rem' },
                  textAlign: 'center'
                }}>
                  <Chip
                    label={product.product_type}
                    size="small"
                    sx={{
                      backgroundColor: getProductTypeColor(product.product_type) + '22',
                      color: getProductTypeColor(product.product_type),
                      fontWeight: '600',
                      border: `1.5px solid ${getProductTypeColor(product.product_type)}55`,
                      fontSize: '0.75rem',
                      userSelect: 'none'
                    }}
                  />
                </TableCell>

                <TableCell sx={{ padding: { xs: '8px 6px', md: '12px 8px' }, textAlign: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, product)}
                    sx={{
                      backgroundColor: '#f9fafb',
                      border: '1.5px solid #e5e7eb',
                      color: '#6b7280',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        color: '#2563eb',
                        transform: 'scale(1.1)',
                        boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)'
                      }
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              backgroundColor: '#fff',
              borderRadius: 2,
              boxShadow: '0 12px 24px rgba(0, 0, 0, 0.12)',
              border: '1px solid #e5e7eb',
              padding: 1,
              minWidth: 260
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {selectedProduct && (
            <Box display="flex" flexWrap="wrap" gap={1} px={1} py={1}>
              {[
                                      {
                          title: 'Edit Product',
                          icon: (
                            <ErrorIndicatorIcon
                              icon={<Edit fontSize="small" />}
                              type="edit"
                              status={status}
                              product={selectedProduct}  // ✅ FIX: replace 'product' with correct variable
                            />
                          ),
                          onClick: () => {
                            onEdit(selectedProduct);
                            handleMenuClose();
                          },
                          colorBg: '#eff6ff',
                          color: '#2563eb',
                          border: '#dbeafe'
                        },
                {
                  title: 'Add Key Exhibitor',
                  icon: <ErrorIndicatorIcon icon={<VpnKey fontSize="small" />} type="key_exhibitor" status={status} />,
                  onClick: () => { onAddKeyExhibitor(selectedProduct); handleMenuClose(); },
                  colorBg: '#faf5ff',
                  color: '#7c3aed',
                  border: '#e9d5ff'
                },
                {
                  title: 'Add SEO Settings',
                   icon: <ErrorIndicatorIcon icon={<Search fontSize="small" />} type="seo" status={status} />,
                  onClick: () => { onAddSeo(selectedProduct); handleMenuClose(); },
                  colorBg: '#f0fdf4',
                  color: '#16a34a',
                  border: '#dcfce7'
                },
                {
                  title: 'Delete Product',
                  icon: <Delete fontSize="small" />,
                  onClick: () => { onDeleteProduct(selectedProduct.ex_product_id); handleMenuClose(); },
                  colorBg: '#fef2f2',
                  color: '#dc2626',
                  border: '#fecaca'
                },
                {
                  title: 'Manage Images',
                   icon: <ErrorIndicatorIcon icon={<Image fontSize="small" />} type="shop_images" status={status} />,
                  onClick: () => handleOpenImageModal({ productId: selectedProduct.ex_product_id, woocommerce_product_id: selectedProduct.woocommerce_product_id }),
                  colorBg: '#fffbeb',
                  color: '#d97706',
                  border: '#fed7aa'
                }
              ].map(({ title, icon, onClick, colorBg, color, border }, idx) => (
                  <Tooltip key={idx} title={title} arrow>
                    <IconButton
                      size="small"
                      onClick={onClick}
                      sx={{
                        backgroundColor: colorBg,
                          color: color,
                          border: `1.5px solid ${border}`,
                      borderRadius: '50%',  // bubble effect
                      padding: '6px',       // increase padding for bubble
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        backgroundColor: border,
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    {title === 'Edit Product' ? React.cloneElement(icon, { sx: { color: '#2563eb' } }) :
                     title === 'Delete Product' ? React.cloneElement(icon, { sx: { color: '#dc2626' } }) : icon}
                  </IconButton>
                  </Tooltip>
              ))}
            </Box>
          )}
        </Menu>

        <ShopImageModal
          open={imageModalOpen}
          onClose={handleCloseImageModal}
          product_id={selectedProductIdForImages?.productId}
          woocommerce_product_id={selectedProductIdForImages?.woocommerce_product_id}
        />
      </TableContainer>
    </Box>
  );
};

export default ExhibitorProductTable;